/**
 * @fileoverview Reusable interactive guided-tour overlay primitive (Phase 5, Workstream K1).
 * @module ui-admin/components/GuidedTour
 *
 * Responsibilities: - Render a live, full-viewport overlay on top of the real page that spotlights
 *                     one target element at a time (orange ring + dimmed surroundings) and shows a
 *                     tooltip card with Back / Next / Skip controls.
 *                   - Keep the spotlight aligned with its target as the page scrolls or resizes,
 *                     and let the user interact with the highlighted element through the cut-out.
 * Dependencies:     - React, react-dom (createPortal), lucide-react icons; `TourStep` from help types.
 * Architecture:     - Self-contained custom implementation (no third-party tour library). Portals
 *                     into document.body at z-index 9999. The spotlight is produced with a CSS
 *                     box-shadow inset spread so the target itself is never mutated. Presentation
 *                     only — no Dexie, no API, no mock data.
 * Notes:            - Positions come from getBoundingClientRect(); in non-DOM/test environments a
 *                     zero rect simply centres the tooltip. Escape and final Next both complete.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import type { TourStep } from '../../help/types';
import { useTheme, useThemeCSS } from '../../contexts/ThemeContext';

/**
 * @contract GuidedTourProps
 * Purpose: Drive the guided-tour overlay.
 * Fields:
 * - steps: ordered tour steps; each spotlights a `targetSelector` (empty = centred).
 * - active: whether the tour is running (renders nothing when false or steps is empty).
 * - onComplete: called when the user finishes the last step, skips, or presses Escape.
 */
export interface GuidedTourProps {
  steps: TourStep[];
  active: boolean;
  onComplete: () => void;
}

interface Rect { top: number; left: number; width: number; height: number }

const PAD = 6;
const CARD_WIDTH = 320;

/**
 * @process measure
 * Steps:   1. Bail with null when selector is empty or DOM is unavailable.
 *          2. querySelector the target; bail if not found.
 *          3. Read getBoundingClientRect; bail if the rect is a zero-rect (hidden/unmounted).
 * Inputs:  CSS selector string.
 * Outputs: Rect (top/left/width/height) or null.
 * Failure Handling: Returns null — caller treats a missing target as a centred step.
 * Idempotency: REQUIRED — pure DOM read, no side effects.
 */
function measure(selector: string): Rect | null {
  if (!selector || typeof document === 'undefined') return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0 && r.top === 0 && r.left === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * @process cardPosition
 * Steps:   1. SSR guard — return zero position when window is unavailable.
 *          2. Centre branch — vertically centre via translateY(-50%) so the card
 *             never overflows the viewport regardless of content height.
 *          3. Directional branches — position the card next to the spotlight rect:
 *             bottom/right: offset from the rect's trailing edge;
 *             top: use CSS bottom so the card grows upward away from the target;
 *             left: offset leftward by CARD_WIDTH.
 *          4. Clamp left/top to keep the card within 8px of all viewport edges.
 * Inputs:  Target Rect (or null for centred), TourStep placement.
 * Outputs: React.CSSProperties with position coordinates.
 * Failure Handling: null rect falls through to the centre branch.
 * Idempotency: REQUIRED — pure computation, no side effects.
 */
function cardPosition(rect: Rect | null, placement: TourStep['placement']): React.CSSProperties {
  if (typeof window === 'undefined') return { top: 0, left: 0 };
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  if (!rect || placement === 'center') {
    return {
      top: '50%',
      left: Math.max(8, vw / 2 - CARD_WIDTH / 2),
      transform: 'translateY(-50%)',
      maxHeight: vh - 16,
    };
  }
  const gap = 14;
  let top = rect.top;
  let left = rect.left;
  switch (placement) {
    case 'bottom': top = rect.top + rect.height + gap; left = rect.left; break;
    case 'top': {
      const bottomEdge = vh - (rect.top - gap);
      return {
        bottom: bottomEdge,
        left: Math.min(Math.max(8, rect.left), vw - CARD_WIDTH - 8),
        maxHeight: Math.max(0, vh - bottomEdge - 8),
      };
    }
    case 'right': top = rect.top; left = rect.left + rect.width + gap; break;
    case 'left': top = rect.top; left = rect.left - CARD_WIDTH - gap; break;
  }
  left = Math.min(Math.max(8, left), vw - CARD_WIDTH - 8);
  top = Math.min(Math.max(8, top), vh - 8);
  return { top, left, maxHeight: vh - top - 8 };
}

/**
 * @component GuidedTour
 * Purpose:     Live overlay that walks the user through real on-page elements, step by step.
 * Props:       GuidedTourProps.
 * Behavior:    Spotlights each step's target with an orange ring; Back/Next navigate; Skip and
 *              Escape (and the final Next) call onComplete. Repositions on scroll/resize.
 * Data Source: none (presentation). Reads element rects from the live DOM only.
 * Constraints: no API/Dexie access; no mock data; no third-party tour dependency.
 */
export function GuidedTour({ steps, active, onComplete }: GuidedTourProps): React.ReactPortal | null {
  const { theme } = useTheme();
  const themeCssString = useThemeCSS();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const indexRef = useRef(index);
  indexRef.current = index;
  const cardRef = useRef<HTMLDivElement>(null);

  const total = steps.length;
  const step = steps[index];
  const isLast = index === total - 1;

  // Reset to the first step whenever the tour (re)starts.
  useEffect(() => {
    if (active) setIndex(0);
  }, [active]);

  const recompute = useCallback(() => {
    const current = steps[indexRef.current];
    setRect(current ? measure(current.targetSelector) : null);
  }, [steps]);

  useLayoutEffect(() => {
    if (!active || !step) return;
    recompute();
  }, [active, index, step, recompute]);

  useEffect(() => {
    if (!active) return;
    const onChange = () => recompute();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onChange) : null;
    if (ro && step?.targetSelector) {
      const el = document.querySelector(step.targetSelector);
      if (el) ro.observe(el);
    }
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      ro?.disconnect();
    };
  }, [active, index, step, recompute]);

  const handleComplete = useCallback(() => {
    setIndex(0);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleComplete();
      } else if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        const i = indexRef.current;
        if (i === steps.length - 1) handleComplete();
        else setIndex((prev) => Math.min(prev + 1, steps.length - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, handleComplete, steps]);

  // Focus trap: confine Tab/Shift+Tab within the card (WAI-ARIA modal dialog pattern).
  useEffect(() => {
    if (!active || !cardRef.current) return;
    const card = cardRef.current;
    card.focus();
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = card.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === card)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || document.activeElement === card)) {
        e.preventDefault();
        first.focus();
      }
    };
    card.addEventListener('keydown', onTab);
    return () => card.removeEventListener('keydown', onTab);
  }, [active, index]);

  if (!active || total === 0 || !step) return null;
  if (typeof document === 'undefined') return null;

  const next = () => (isLast ? handleComplete() : setIndex((i) => Math.min(i + 1, total - 1)));
  const back = () => setIndex((i) => Math.max(i - 1, 0));
  const showSkip = step.showSkip !== false;

  // Spotlight: a transparent rect over the target with a huge box-shadow that dims everything else
  // plus an orange ring. When no rect is available, dim the whole viewport (centred step).
  const spotlightStyle: React.CSSProperties = rect
    ? {
        position: 'fixed',
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        borderRadius: 8,
        boxShadow: `0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 3px ${theme.accent}`,
        pointerEvents: 'none',
        transition: 'all 0.2s ease',
      }
    : {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      };

  return createPortal(
    <div data-testid="guided-tour" style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}>
      <style>{themeCssString}</style>
      <div data-testid="guided-tour-spotlight" style={spotlightStyle} />
      <div
        ref={cardRef}
        role="dialog"
        aria-label="Guided tour"
        aria-modal="true"
        tabIndex={-1}
        data-testid="guided-tour-card"
        className="theme-grid rounded-xl bg-white p-4 shadow-2xl"
        style={{ position: 'fixed', width: CARD_WIDTH, pointerEvents: 'auto', overflowY: 'auto', outline: 'none', background: theme.cardBg, ...cardPosition(rect, step.placement) }}
      >
        <div className="mb-1 flex items-center justify-between">
          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
            Step {index + 1} of {total}
          </span>
          {showSkip && (
            <button
              type="button"
              onClick={handleComplete}
              aria-label="Close tour"
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <h3 className="mb-1 text-sm font-bold text-gray-900">{step.title}</h3>
        <p className="mb-3 text-sm text-gray-600">{step.body}</p>
        <div className="flex items-center justify-between">
          {showSkip ? (
            <button type="button" onClick={handleComplete} className="text-xs text-gray-500 hover:text-gray-700">
              Skip tour
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={back}
              disabled={index === 0}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700"
            >
              {isLast ? (<><Check className="h-3.5 w-3.5" /> Finish</>) : (<>Next <ArrowRight className="h-3.5 w-3.5" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default GuidedTour;
